import { useState, useEffect, useRef } from "react";
import React from "react";
import DocumentUpload from "../../components/DocumentUpload";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import { getStateFromGST } from "../../constants/gstStateCodes";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {} from // validateAgreementStartDate, // TODO: uncomment when agreement date fields are added to UI
// validateAgreementEndDate,   // TODO: uncomment when agreement date fields are added to UI
"../../utils/dateUtils";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
} from "../../utils/validationUtils";
import { fetchPincodeDetailsAction } from "../../actions/pincodeAction";
// import { useDepartmentOptions } from "../../actions/fetchDepartmentListAction"; // TODO: re-enable when department dropdown is added
import { sendEntityFormDataAction } from "../../actions/createEntityFormDataActions";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { URL_CONFIG } from "../../config/urlConfig";
import { getUserData } from "../../utils/authnicate";
import {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  ALL_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
} from "./UploadDetailsFields";

export {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
};

// ── Entity-type required documents section ────────────────────────────────────

export const EntityDocumentsSection = ({
  entityType,
  entityFiles,
  onEntityFileChange,
  entityDocInputs = {},
  onEntityDocInputChange,
}) => {
  const docs = ENTITY_TYPE_DOCUMENTS[entityType];
  if (!docs || docs.length === 0) return null;
  return (
    <div className="entity-docs-section">
      <p className="entity-docs-note">
        Required documents for <strong>{entityType.replace(/_/g, " ")}</strong>
      </p>
      <div className="entity-docs-list">
        {docs.map((doc) => (
          <div key={doc.key} className="entity-doc-row">
            {doc.inputField && (
              <InputField
                name={doc.inputField.name}
                label={`${doc.label} Number`}
                type="text"
                value={entityDocInputs[doc.inputField.name] || ""}
                onChange={(e) =>
                  onEntityDocInputChange &&
                  onEntityDocInputChange(doc.inputField.name, e.target.value)
                }
                placeholder={doc.inputField.placeholder}
                maxLength={doc.inputField.maxLength}
                required={doc.required}
              />
            )}
            <DocumentUpload
              label={`Upload ${doc.label}`}
              name={doc.key}
              file={entityFiles[doc.key] || null}
              onChange={(e) => onEntityFileChange(doc.key, e.target.files[0])}
              required={doc.required}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Field renderer ─────────────────────────────────────────────────────────────

export const renderFields = (
  fields,
  formData,
  errors,
  handleChange,
  handleBlur,
) =>
  fields.map((field) => {
    const show =
      !field.conditionalOn ||
      formData[field.conditionalOn] === field.conditionalValue;
    if (!show) return null;
    const value = formData[field.name] || "";
    const error = errors[field.name] || "";
    if (field.type === "select") {
      return (
        <DropDown
          key={field.name}
          name={field.name}
          value={value}
          onChange={handleChange}
          label={field.label}
          options={field.options}
          error={error}
          required={field.required}
          fullWidth
        />
      );
    }
    return (
      <InputField
        key={field.name}
        name={field.name}
        label={field.label}
        type={field.type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={field.placeholder}
        error={error}
        required={field.required}
        maxLength={field.maxLength}
        rows={field.rows}
        readOnly={field.readOnly}
      />
    );
  });

// ── Initial state ──────────────────────────────────────────────────────────────

export const initialDetailsState = {
  parentName: "",
  tradeName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  entityType: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  utilityType: "",
  utilityProviderName: "",
  utilityAccountNumber: "",
  utilityAddress: "",
  isMSMERegistered: "",
  msmeNumber: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  beneficiaryName: "",
  thirdPartyName: "",
  thirdPartyRelation: "",
  thirdPartyAccountNumber: "",
  thirdPartyIfscCode: "",
  thirdPartyBankName: "",
  agreementStartDate: "",
  agreementEndDate: "",
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useUploadDetails = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(initialDetailsState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GST entries state
  const [gstEntries, setGstEntries] = useState([
    { gstNumber: "", gstState: "", gstAddress: "" },
  ]);
  const [gstFiles, setGstFiles] = useState([null]);
  const [gstErrors, setGstErrors] = useState([""]);

  // Document file states
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [entityFiles, setEntityFiles] = useState({});
  const [entityDocInputs, setEntityDocInputs] = useState({});
  const [bankFiles, setBankFiles] = useState({});

  const handleEntityFileChange = (key, file) =>
    setEntityFiles((prev) => ({ ...prev, [key]: file }));

  const handleEntityDocInputChange = (name, value) =>
    setEntityDocInputs((prev) => ({ ...prev, [name]: value }));

  const handleBankFileChange = (key, file) =>
    setBankFiles((prev) => ({ ...prev, [key]: file }));

  // const departmentOptions = useDepartmentOptions(); // TODO: use when department dropdown is added
  const pincodeRef = useRef(null);

  const [beneficiaryMatchStatus, setBeneficiaryMatchStatus] = useState(null);
  // null = not checked yet | "match" | "mismatch"

  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    formData.tradeName.trim() !== "" &&
    formData.beneficiaryName.trim() !== "" &&
    formData.tradeName.trim().toLowerCase() !==
      formData.beneficiaryName.trim().toLowerCase();

  // URL params from step 1
  const entityCategory = searchParams.get("entityCategory") || "";
  // const entityLOBType = searchParams.get("entityLOBType") || "";         // TODO: show in summary
  // const lobNationality = searchParams.get("lobNationality") || "Domestic"; // TODO: include in payload when needed
  // const aaDepartmentFunction = searchParams.get("aaDepartmentFunction") || ""; // TODO: include in payload when needed
  const contactPersonEmail = searchParams.get("contactPersonEmail") || "";
  const spocEmailAddress = searchParams.get("spocEmailAddress") || "";
  const entityNameFromUrl = searchParams.get("EntityName") || "";
  console.log("Entity name : ", entityNameFromUrl);

  // GST entry handlers
  const addGstEntry = () => {
    setGstEntries((prev) => [
      ...prev,
      { gstNumber: "", gstState: "", gstAddress: "" },
    ]);
    setGstFiles((prev) => [...prev, null]);
    setGstErrors((prev) => [...prev, ""]);
  };
  const removeGstEntry = (index) => {
    setGstEntries((prev) => prev.filter((_, i) => i !== index));
    setGstFiles((prev) => prev.filter((_, i) => i !== index));
    setGstErrors((prev) => prev.filter((_, i) => i !== index));
  };
  const handleGstChange = (index, field, value) => {
    setGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          // Clear error while user is still typing
          setGstErrors((errs) => errs.map((e, ei) => (ei === index ? "" : e)));
        }
        return updated;
      }),
    );
  };
  // On blur: validate and show error if invalid
  const handleGstBlur = (index) => {
    const gstNumber = gstEntries[index]?.gstNumber || "";
    if (!gstNumber) return;
    const result = validateGST(gstNumber);
    setGstErrors((prev) =>
      prev.map((e, ei) => (ei === index ? (result === true ? "" : result) : e)),
    );
  };
  const handleGstFileChange = (index, file) => {
    setGstFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  // Seed entity name from URL param (editable after mount)
  useEffect(() => {
    if (entityNameFromUrl) {
      setFormData((prev) => ({ ...prev, parentName: entityNameFromUrl }));
    }
  }, [entityNameFromUrl]);

  // Pincode auto-fill
  useEffect(() => {
    const pin = (formData.pinCode || "").replace(/\D/g, "");
    if (pin.length !== 6 || pincodeRef.current === pin) return;
    pincodeRef.current = pin;
    fetchPincodeDetailsAction(pin).then((result) => {
      if (result.success) {
        setFormData((prev) => ({
          ...prev,
          city: result.city,
          State: result.state,
        }));
        setErrors((prev) => {
          const e = { ...prev };
          delete e.pinCode;
          return e;
        });
      } else {
        setFormData((prev) => ({ ...prev, city: "", State: "" }));
        setErrors((prev) => ({
          ...prev,
          pinCode: "Invalid pin code, please enter the correct pin code.",
        }));
      }
    });
  }, [formData.pinCode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "CompanypanNumber",
      "ifscCode",
      "thirdPartyIfscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    const digitsOnlyFields = [
      "contactPersonMobile",
      "accountNumber",
      "thirdPartyAccountNumber",
    ];
    if (digitsOnlyFields.includes(name) && typeof newValue === "string")
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      newValue = newValue.replace(/\D/g, "").slice(0, 6);
      // Reset ref so a new 6-digit pin triggers a fresh fetch
      if (newValue.length < 6) pincodeRef.current = null;
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
          "beneficiaryName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Reset match status when trade name or beneficiary name changes
    if (name === "tradeName" || name === "beneficiaryName") {
      setBeneficiaryMatchStatus(null);
    }

    if (type !== "checkbox" && errors[name]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }

    // Agreement date cross-validation — TODO: uncomment when date fields are added to UI
    // if (name === "agreementStartDate" || name === "agreementEndDate") {
    //   const updatedStart = name === "agreementStartDate" ? newValue : formData.agreementStartDate;
    //   const updatedEnd = name === "agreementEndDate" ? newValue : formData.agreementEndDate;
    //   setErrors((prev) => {
    //     const e = { ...prev };
    //     const se = validateAgreementStartDate(updatedStart, updatedEnd);
    //     const ee = validateAgreementEndDate(updatedEnd, updatedStart);
    //     if (se) e.agreementStartDate = se; else delete e.agreementStartDate;
    //     if (ee) e.agreementEndDate = ee; else delete e.agreementEndDate;
    //     return e;
    //   });
    // }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;

    // Beneficiary vs trade name match check on blur of either field
    if (name === "beneficiaryName" || name === "tradeName") {
      const trade = (name === "tradeName" ? value : formData.tradeName)
        .trim()
        .toLowerCase();
      const beneficiary = (
        name === "beneficiaryName" ? value : formData.beneficiaryName
      )
        .trim()
        .toLowerCase();
      if (trade && beneficiary) {
        setBeneficiaryMatchStatus(trade === beneficiary ? "match" : "mismatch");
      }
      return;
    }

    const validators = {
      CompanypanNumber: validatePAN,
      contactPersonMobile: validateMobile,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
      thirdPartyIfscCode: validateIFSC,
      thirdPartyAccountNumber: validateAccountNumber,
    };
    const validator = validators[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) setErrors((prev) => ({ ...prev, [name]: result }));
      else
        setErrors((prev) => {
          const e = { ...prev };
          delete e[name];
          return e;
        });
    }
  };

  const validate = () => {
    const newErrors = {};
    ALL_FIELDS.forEach((field) => {
      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (field.required && (!value || value.toString().trim() === "")) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });

    if (formData.isGSTRegistered === "no") {
      UTILITY_FIELDS.forEach((field) => {
        if (field.required && !formData[field.name]?.trim())
          newErrors[field.name] = `${field.label} is required`;
      });
    }

    if (showThirdParty) {
      THIRD_PARTY_FIELDS.forEach((field) => {
        const value = formData[field.name];
        if (field.required && (!value || value.toString().trim() === "")) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (field.validation && value) {
          const result = field.validation(value);
          if (result !== true) newErrors[field.name] = result;
        }
      });
    }

    // Agreement date validation — TODO: uncomment when date fields are added to UI
    // const se = validateAgreementStartDate(formData.agreementStartDate, formData.agreementEndDate);
    // const ee = validateAgreementEndDate(formData.agreementEndDate, formData.agreementStartDate);
    // if (se) newErrors.agreementStartDate = se;
    // if (ee) newErrors.agreementEndDate = ee;
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);
    try {
      const loggedInUser = getUserData();
      const now = new Date().toISOString();
      const documentUploadURL = `${URL_CONFIG.BASE_URL}/upload-documents?`;

      const payload = {
        entityCategory,
        // lobType: entityLOBType,       // TODO: uncomment when entityLOBType is re-enabled
        // lobNationality,               // TODO: uncomment when lobNationality is re-enabled
        entityId: "",
        entityName: formData.parentName,
        tradeName: formData.tradeName,
        entityType: formData.entityType,
        entityAddress: formData.address,
        pinCode: parseInt(formData.pinCode, 10) || 0,
        city: formData.city,
        state: formData.State,
        // aaDepartmentFunctionId: parseInt(aaDepartmentFunction, 10) || 0, // TODO: re-enable when param is used
        natureOfBusiness: formData.natureOfBusiness,
        companyPAN: formData.CompanypanNumber,
        isGSTRegistered: convertToBoolean(formData.isGSTRegistered),
        gstEntries: formData.isGSTRegistered === "yes" ? gstEntries : [],
        isRegisteredUnderMSME: convertToBoolean(formData.isMSMERegistered),
        msmeNumber:
          formData.isMSMERegistered === "yes" ? formData.msmeNumber : "",
        contactPerson: formData.contactPersonName,
        designation: formData.contactPersonDesignation,
        mobileNumber: formData.contactPersonMobile,
        emailAddress: contactPersonEmail,
        bankName: formData.bankName || "",
        accountNumber: formData.accountNumber || "",
        ifscCode: formData.ifscCode || "",
        branchNameAndAddress: formData.branchName || "",
        beneficiaryName: formData.beneficiaryName || "",
        thirdPartyPayment: showThirdParty,
        thirdPartyDetails: showThirdParty
          ? {
              name: formData.thirdPartyName,
              relation: formData.thirdPartyRelation,
              accountNumber: formData.thirdPartyAccountNumber,
              ifscCode: formData.thirdPartyIfscCode,
              bankName: formData.thirdPartyBankName,
            }
          : null,
        documentUploadURL,
        createdOn: now,
        modifiedOn: now,
        // agreementStartDate: formData.agreementStartDate || null, // TODO: re-enable when UI fields added
        // agreementEndDate: formData.agreementEndDate || null,     // TODO: re-enable when UI fields added
        spocEmailAddress,
        createdBy: loggedInUser?.userName || "",
        updatedBy: loggedInUser?.userName || "",
      };

      const result = await sendEntityFormDataAction(payload);
      if (result?.success) {
        toast.success("Entity created successfully!");
        setFormData(initialDetailsState);
        setErrors({});
        setUtilityFile(null);
        setMsmeFile(null);
        setThirdPartyFile(null);
        setEntityFiles({});
        setEntityDocInputs({});
        setGstEntries([{ gstNumber: "", gstState: "", gstAddress: "" }]);
        setGstFiles([null]);
        setGstErrors([""]);
        setBankFiles({});
      } else {
        toast.error(result?.error || "Failed to create entity");
      }
    } catch (err) {
      toast.error("An error occurred while submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    errors,
    isSubmitting,
    // departmentOptions, // TODO: re-enable when department dropdown is added
    handleChange,
    handleBlur,
    handleSubmit,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    beneficiaryMatchStatus,
    entityCategory,
    // entityLOBType,   // TODO: re-enable when shown in UI
    contactPersonEmail,
    spocEmailAddress,
  };
};
